import { Box, Chip, Divider, Stack, Typography } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { PermissionDto, PERMISSION_AUDIENCE_RU } from '@sr/dto';
import { THEMES, chipSx } from './permissions-style-mapper';

type AudienceKey = keyof typeof PERMISSION_AUDIENCE_RU;

const getAudienceRu = (key?: string) => (key && key !== '__none__' ? (PERMISSION_AUDIENCE_RU[key as AudienceKey] ?? key) : 'Без аудитории');

const mutedChipSx = {
  borderColor: (t: Theme) => t.palette.divider,
  color: (t: Theme) => t.palette.text.disabled,
  bgColor: 'transparent',
};

const groupByAudience = (perms: PermissionDto[]) => {
  const map = new Map<string, PermissionDto[]>();
  for (const p of perms) {
    const key = p.audience ?? '__none__';
    (map.get(key) ?? map.set(key, []).get(key)!).push(p);
  }
  return [...map.entries()]
    .sort(([a], [b]) => getAudienceRu(a).localeCompare(getAudienceRu(b)))
    .map(([k, list]) => [k, list.sort((x, y) => x.title.localeCompare(y.title))] as const);
};

type PermissionGroupsViewProps = {
  permissions: PermissionDto[];
  mode?: 'view';
  emptyLabel?: string;
};

type PermissionGroupsEditProps = {
  permissions: PermissionDto[];
  mode: 'edit';
  selectedIds: Set<number>;
  onToggle: (id: number) => void;
  emptyLabel?: string;
};

export type PermissionGroupsProps = PermissionGroupsViewProps | PermissionGroupsEditProps;

export const PermissionGroups = (props: PermissionGroupsProps) => {
  const { permissions } = props;
  const mode = props.mode ?? 'view';
  const emptyLabel = props.emptyLabel ?? 'Пермиссии не найдены';
  const groups = groupByAudience(permissions);

  if (groups.length === 0) {
    return <Typography color="text.secondary">{emptyLabel}</Typography>;
  }

  const isEdit = mode === 'edit';

  return (
    <Stack spacing={1.25}>
      {groups.map(([audKey, list]) => {
        const theme = THEMES[audKey as keyof typeof THEMES];
        const color = theme?.color ?? '#5e6a7d';
        const icon = theme?.icon;

        return (
          <Box key={audKey}>
            <Typography variant="overline" sx={{ color, fontWeight: 700 }}>
              {getAudienceRu(audKey)}
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 0.5 }}>
              {list.map((p) => {
                if (isEdit) {
                  const isSelected = (props as PermissionGroupsEditProps).selectedIds.has(p.id);
                  return (
                    <Chip
                      key={p.id}
                      label={p.title}
                      size="small"
                      variant="outlined"
                      icon={icon}
                      clickable
                      onClick={() => (props as PermissionGroupsEditProps).onToggle(p.id)}
                      sx={isSelected ? chipSx(color) : mutedChipSx}
                    />
                  );
                }
                return <Chip key={p.id} label={p.title} size="small" variant="outlined" icon={icon} sx={chipSx(color)} />;
              })}
            </Stack>
            <Divider sx={{ mt: 1.25 }} />
          </Box>
        );
      })}
    </Stack>
  );
};
